// Add a secret from a type template (docs/spike-secrets.md "The secret, as the user sees
// it"), the second step of the add drawer. The metadata is ordinary form state; the value
// inputs are uncontrolled and read only at submit (ValueInputs).
import { type FormEvent, useState } from "react";
import { Button, Input } from "rizzui";

import { useT } from "../i18n";
import { createSecret } from "./api";
import { clearValues, formReader } from "./form";
import type { Signer } from "./requests";
import { DrawerBody, DrawerFooter, Hinted } from "./SecretDrawer";
import { AgentReadable, KeptChoice } from "./SecretOptions";
import { type Credential, DEFAULT_HEADER, type SecretDraft, type Template } from "./templates";
import { ValueInputs } from "./ValueInputs";

interface Props {
  signer: Signer;
  /** The signing device's name, for the footer. */
  signerName: string;
  orgId: string;
  template: Template;
  onSaved: (credential: Credential) => void;
  onError: (message: string) => void;
  onCancel: () => void;
}

export function AddSecretForm({ signer, signerName, orgId, template: t, onSaved, onError, onCancel }: Props) {
  const { common, secrets } = useT();
  const copy = secrets.forms.add;
  const [name, setName] = useState("");
  const [scope, setScope] = useState("");
  const [hosts, setHosts] = useState("");
  const [header, setHeader] = useState(DEFAULT_HEADER);
  const [workerExposed, setWorkerExposed] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const draft: SecretDraft = { name, template: t, scope, hosts, header, workerExposed };
    setBusy(true);
    try {
      const saved = await createSecret(signer, orgId, draft, formReader(form));
      clearValues(form, t.value);
      setName("");
      setScope("");
      setHosts("");
      setWorkerExposed(false);
      onSaved(saved);
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="flex min-h-0 flex-1 flex-col" aria-label={copy.title} onSubmit={(e) => void onSubmit(e)}>
      <DrawerBody>
        <Hinted hint={copy.nameHint}>
          <Input label={copy.name} value={name} placeholder={t.namePlaceholder} onChange={(e) => setName(e.target.value)} required maxLength={128} />
        </Hinted>
        <ValueInputs shape={t.value} />
        {t.scope ? (
          <Hinted hint={copy.scopeHint}>
            <Input
              label={copy.scopeLabel(t.scope.label)}
              value={scope}
              placeholder={t.scope.placeholder}
              onChange={(e) => setScope(e.target.value)}
            />
          </Hinted>
        ) : null}
        {t.injection ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label={copy.hosts} value={hosts} placeholder="api.stripe.com" onChange={(e) => setHosts(e.target.value)} required />
            <Input
              label={copy.header}
              value={header}
              onChange={(e) => setHeader(e.target.value)}
              inputClassName="font-mono text-xs"
              required
            />
          </div>
        ) : null}
        <KeptChoice />
        <AgentReadable checked={workerExposed} onChange={setWorkerExposed} />
        <p className="text-xs text-gray-500">{copy.sealedNote}</p>
      </DrawerBody>
      <DrawerFooter signedBy={signerName}>
        <Button variant="outline" onClick={onCancel} disabled={busy}>
          {common.actions.cancel}
        </Button>
        <Button type="submit" isLoading={busy}>
          {copy.save}
        </Button>
      </DrawerFooter>
    </form>
  );
}
